import { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import { NewReaction } from '@/types/models';
import { DEFAULT_ERROR } from '@/constants/errors';
import { REACTIONS_STATE_INITIAL_DATA_MOCK } from '@/redux/reactions/reactions.mock';
import { BioEntitySearchedIdObject } from '@/redux/bioEntity/bioEntity.types';
import { REACTIONS_INITIAL_STATE } from './reactions.constants';
import { getReactionsForModel } from './reactions.thunks';
import { ReactionsState } from './reactions.types';

export const getReactionsForModelReducer = (
  builder: ActionReducerMapBuilder<ReactionsState>,
): void => {
  builder.addCase(getReactionsForModel.pending, (state, action) => {
    const modelId = action.meta.arg;
    if (state.data[modelId]) {
      state.data[modelId].loading = 'pending';
    } else {
      state.data[modelId] = { ...REACTIONS_STATE_INITIAL_DATA_MOCK, loading: 'pending' };
    }
  });
  builder.addCase(getReactionsForModel.fulfilled, (state, action) => {
    const modelId = action.meta.arg;
    const data = action.payload || [];
    if (state.data[modelId]) {
      state.data[modelId].data = data;
      state.data[modelId].loading = 'succeeded';
    } else {
      state.data[modelId] = { data, loading: 'pending', error: DEFAULT_ERROR };
    }
  });
  builder.addCase(getReactionsForModel.rejected, (state, action) => {
    const modelId = action.meta.arg;
    if (state.data[modelId]) {
      state.data[modelId].loading = 'failed';
    } else {
      state.data[modelId] = { ...REACTIONS_STATE_INITIAL_DATA_MOCK, loading: 'failed' };
    }
  });
};

export const resetHighlightReactionsDataReducer = (state: ReactionsState): void => {
  state.highlight.data = REACTIONS_INITIAL_STATE.highlight.data;
  state.highlight.error = REACTIONS_INITIAL_STATE.highlight.error;
  state.highlight.loading = REACTIONS_INITIAL_STATE.highlight.loading;
};

export const setLoadingHighlightReactionsDataReducer = (state: ReactionsState): void => {
  state.highlight.loading = 'pending';
};

export const setHighlightReactionsReducer = (
  state: ReactionsState,
  action: PayloadAction<Array<NewReaction>>,
): void => {
  state.highlight.data = action.payload.map(reaction => {
    return {
      data: [
        {
          reaction,
          perfect: true,
        },
      ],
      loading: 'succeeded',
      error: DEFAULT_ERROR,
      searchQueryElement: reaction.id.toString(),
    };
  });
};

export const setHighlightReactionsByIdsReducer = (
  state: ReactionsState,
  action: PayloadAction<{
    searchedReactions: Array<BioEntitySearchedIdObject>;
  }>,
): void => {
  const { searchedReactions } = action.payload;
  const highlightReactions: Record<string, Array<{ reaction: NewReaction; perfect: boolean }>> = {};
  searchedReactions.forEach(searchedReaction => {
    const highlightReaction = state.data[searchedReaction.modelId]?.data?.find(
      reactionItem => reactionItem.id === searchedReaction.id,
    );
    if (highlightReaction && highlightReactions[searchedReaction.searchQuery]) {
      highlightReactions[searchedReaction.searchQuery].push({
        reaction: highlightReaction,
        perfect: searchedReaction.perfect,
      });
    } else if (highlightReaction) {
      highlightReactions[searchedReaction.searchQuery] = [
        {
          reaction: highlightReaction,
          perfect: searchedReaction.perfect,
        },
      ];
    }
  });
  Object.entries(highlightReactions).forEach(([query, highlightReactionsForQuery]) => {
    const stateHighlightReactions = state.highlight.data.find(
      highlightReaction => highlightReaction.searchQueryElement === query,
    );
    if (stateHighlightReactions) {
      stateHighlightReactions.data = highlightReactionsForQuery;
      stateHighlightReactions.loading = 'succeeded';
    } else {
      state.highlight.data.push({
        data: highlightReactionsForQuery,
        loading: 'succeeded',
        error: DEFAULT_ERROR,
        searchQueryElement: query,
      });
    }
    state.highlight.loading = 'succeeded';
  });
};
